import dayjs from "dayjs";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { Badge, Button, Card, CardBody, Col, Input, Row } from "reactstrap";
import { useGetClientBookings } from "../../api/booking";
import ActionMenu from "../../components/Common/ActionMenu";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import Paginations from "../../components/Common/Pagination";
import Spinners from "../../components/Common/Spinner";
import TableContainer from "../../components/Common/TableContainer";
import { selectRole, selectUser } from "../../store/auth/selectors";
import {
  // canAccessInviteWorkers,
  canAccessWorkersManagement,
  hasUnpaidInvoices,
} from "./clientBookingAccess";

const STATUS_COLOR_MAP = {
  processing: "primary",
  pending: "warning",
  approved: "success",
  rejected: "danger",
  cancelled: "secondary",
};

const ClientBookings = () => {
  document.title = "View Bookings | Client";

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const navigate = useNavigate();

  const role = useSelector(selectRole);
  const user = useSelector(selectUser);

  const { mutate, data, isLoading, isPending, error } = useGetClientBookings();
  const loading = isLoading || isPending;

  const lastFetchKeyRef = useRef("");
  const fetchKey = JSON.stringify({ page, limit, status, search });

  useEffect(() => {
    if (lastFetchKeyRef.current === fetchKey) return;
    lastFetchKeyRef.current = fetchKey;
    mutate({
      page,
      limit,
      status: status || undefined,
      search: search.trim() || undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchKey]);

  const responsePayload = data ?? {};
  const nestedPayload = responsePayload?.data ?? responsePayload;

  const bookings = Array.isArray(nestedPayload)
    ? nestedPayload
    : Array.isArray(nestedPayload?.data)
    ? nestedPayload.data
    : Array.isArray(nestedPayload?.bookings)
    ? nestedPayload.bookings
    : [];

  const total =
    responsePayload?.total ??
    nestedPayload?.total ??
    responsePayload?.data?.length ??
    nestedPayload?.data?.length ??
    nestedPayload?.bookings?.length ??
    bookings.length ??
    0;

  const currencyFormatter = useMemo(
    () =>
      new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency: "GBP",
        minimumFractionDigits: 2,
      }),
    []
  );

  const columns = useMemo(
    () => [
      {
        header: "Booking ID",
        accessorKey: "slug",
        cell: (info) => {
          const slug = info.getValue();
          if (!slug) return "-";
          return (
            <Link
              to={`/client/bookings/${slug}`}
              className="fw-semibold text-primary"
            >
              {slug}
            </Link>
          );
        },
        enableColumnFilter: false,
      },
      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: false,
        cell: (info) => {
          const statusValue = info.getValue();
          const color = STATUS_COLOR_MAP[statusValue] || "info";
          return (
            <Badge className={`bg-${color} text-uppercase`}>
              {statusValue ? statusValue.replace(/_/g, " ") : "unknown"}
            </Badge>
          );
        },
      },
      {
        header: "Booking Type",
        accessorKey: "bookingType",
        enableColumnFilter: false,
        cell: (info) => info.getValue()?.replace(/_/g, " ") || "-",
      },
      {
        header: "Final Amount",
        accessorKey: "finalAmount",
        enableColumnFilter: false,
        cell: (info) => {
          const value = Number(info.getValue());
          if (!value) return "-";
          return currencyFormatter.format(value);
        },
      },
      {
        header: "Shifts",
        accessorKey: "shiftPattern",
        enableColumnFilter: false,
        cell: (info) => {
          const shifts = info.getValue() || [];
          if (!Array.isArray(shifts) || shifts.length === 0) return "-";
          const firstShift = shifts[0];
          const totalDates = shifts.reduce(
            (sum, shift) => sum + (shift.dates?.length || 0),
            0
          );
          return (
            <div className="d-flex flex-column">
              <span className="text-body">
                {firstShift.startTime} - {firstShift.endTime}
              </span>
              <small className="text-muted">
                {shifts.length} shift{shifts.length > 1 ? "s" : ""} ·{" "}
                {totalDates} date
                {totalDates > 1 ? "s" : ""}
              </small>
            </div>
          );
        },
      },
      {
        header: "Location",
        accessorKey: "location.siteName",
        enableColumnFilter: false,
        cell: (info) =>
          info.getValue() || info.row.original.location?.fullAddress || "-",
      },
      {
        header: "PO Reference",
        accessorKey: "poReference",
        enableColumnFilter: false,
        cell: (info) => info.getValue() || "-",
      },
      {
        header: "Flags",
        accessorKey: "hasSendToClient",
        enableColumnFilter: false,
        cell: (info) => {
          const booking = info.row.original;
          return (
            <div className="d-flex flex-wrap gap-1">
              {booking.hasSendToClient && (
                <Badge className="bg-success">Sent</Badge>
              )}
              {booking.hasAcceptedByClient && (
                <Badge className="bg-primary">Accepted</Badge>
              )}
              {booking.hasPORequired && (
                <Badge className="bg-secondary">PO Required</Badge>
              )}
              {booking.hasSmartMatchingComplete ? (
                <Badge className="bg-info">Matching Done</Badge>
              ) : (
                <Badge className="bg-warning text-dark">Matching Pending</Badge>
              )}
            </div>
          );
        },
      },
      {
        header: "Created At",
        accessorKey: "createdAt",
        enableColumnFilter: false,
        cell: (info) =>
          info.getValue()
            ? dayjs(info.getValue()).format("DD MMM YYYY HH:mm")
            : "-",
      },
      {
        header: "Action",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (info) => {
          const row = info.row.original;
          const slug = row.slug;
          const menuItems = [
            {
              key: "view",
              label: "View",
              onClick: () => navigate(`/client/bookings/${slug}`),
            },
          ];

          if (role === "client") {
            menuItems.push({
              key: "edit",
              label: "Edit",
              onClick: () => navigate(`/client/bookings/update/${slug}`),
            });

            // Invite workers action hidden for now
            // if (canAccessInviteWorkers(row)) {
            //   menuItems.push({
            //     key: "invite-workers",
            //     label: "Invite workers",
            //     onClick: () =>
            //       navigate(`/client/bookings/${slug}/invite-workers`),
            //   });
            // }

            if (hasUnpaidInvoices(row)) {
              menuItems.push({
                key: "pay-invoice",
                label: "Pay invoice",
                onClick: () => navigate(`/client/bookings/${slug}/payment`),
              });
            }

            if (canAccessWorkersManagement(row, user?.tier)) {
              menuItems.push({
                key: "workers-management",
                label: "Manage workers",
                onClick: () =>
                  navigate(`/client/bookings/${slug}/workers-management`),
              });
            }
          }

          return <ActionMenu items={menuItems} />;
        },
      },
    ],
    [currencyFormatter, navigate, role, user?.tier]
  );

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs title="Bookings" breadcrumbItem="View Bookings" />

        <Row className="mb-3 g-2 align-items-center">
          <Col md={4}>
            <Input
              type="text"
              placeholder="Search by booking id or PO reference..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setPage(1);
                  setSearch(searchInput.trim());
                }
              }}
            />
          </Col>

          <Col md={3}>
            <Input
              type="select"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setPage(1);
              }}
            >
              <option value="">All Statuses</option>
              <option value="processing">Processing</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="cancelled">Cancelled</option>
            </Input>
          </Col>

          <Col md={2}>
            <Input
              type="select"
              value={limit}
              onChange={(e) => {
                setLimit(Number(e.target.value));
                setPage(1);
              }}
            >
              {[10, 20].map((size) => (
                <option key={size} value={size}>
                  Show {size}
                </option>
              ))}
            </Input>
          </Col>

          <Col md="auto" className="ms-auto d-flex gap-2">
            <Button
              color="primary"
              onClick={() => {
                setPage(1);
                setSearch(searchInput.trim());
              }}
              disabled={loading}
            >
              <i className="mdi mdi-magnify me-1" />
              Search
            </Button>
            <Button
              color="secondary"
              outline
              onClick={() => {
                setStatus("");
                setSearch("");
                setSearchInput("");
                setPage(1);
                setLimit(10);
              }}
              disabled={loading}
            >
              Reset
            </Button>
            <Button
              color="light"
              onClick={() =>
                mutate({
                  page,
                  limit,
                  status: status || undefined,
                  search: search.trim() || undefined,
                })
              }
              disabled={loading}
            >
              <i className="mdi mdi-refresh" />
            </Button>
          </Col>
        </Row>

        <Card>
          <CardBody>
            {loading ? (
              <Spinners />
            ) : error ? (
              <div className="text-danger">Failed to load bookings.</div>
            ) : bookings.length === 0 ? (
              <div className="text-muted text-center py-4">
                No bookings found.
              </div>
            ) : (
              <>
                <TableContainer
                  columns={columns}
                  data={bookings}
                  isPagination={false}
                  isCustomPageSize={false}
                  isGlobalFilter={false}
                  isJobListGlobalFilter={false}
                  tableClass="align-middle table-nowrap dt-responsive nowrap w-100 table-check dataTable no-footer dtr-inline mt-2 border-top"
                  theadClass="table-light"
                  divClassName="table-responsive"
                />

                {total > 0 && (
                  <Paginations
                    total={total}
                    perPageData={limit}
                    currentPage={page}
                    setCurrentPage={setPage}
                    isShowingPageLength
                    paginationDiv="dataTables_paginate paging_simple_numbers pagination-rounded d-flex justify-content-end mt-3"
                    paginationClass="pagination"
                  />
                )}
              </>
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  );
};

export default ClientBookings;
